<template>
  <div
    ref="mainListDropZone"
    class="library-items"
    @drop.prevent="handleFileDrop($event)"
    @dragover.prevent="dragOver"
    @dragleave.prevent="dragLeave"
  >
    <div
      v-if="items.length > 0 || loading"
      ref="itemListScrollable"
      v-infinite-scroll="showPagination ? null : scrollHitBottom"
      class="library-items__scroll"
      infinite-scroll-distance="200"
    >
      <div class="library-items__content">
        <LibraryCardItem
          v-for="(item, index) in processedItems"
          :key="item.id || index"
          :processed-item="item"
          :is-selection-mode-active="showStyleBulkActionsToolbar"
          :selected-style-ids="selectedStyleIds"
          @style-selection-change="$emit('style-selection-change', $event)"
          @delete-item="onDeleteItem"
        />

        <LibraryCardSkeleton
          v-for="index in skeletonCount"
          :key="`library-card-skeleton-${index}`"
          :library-type="libraryType"
        />
      </div>

      <div
        v-if="loading && items.length > 0 && !showPagination"
        class="library-items__loading-more"
      >
        <div class="library-items__loading-inner">
          <span class="library-items__spinner" />
          <span class="library-items__text">
            Loading more {{ libraryType }}s
          </span>
        </div>
      </div>
    </div>

    <ElementAlert
      v-if="items.length === 0 && !loading"
      class="library-items__empty-state"
      :title="
        libraryType !== LIBRARY_TYPE.JOB
          ? `There is no ${libraryType} matching your request`
          : 'No Jobs'
      "
      type="info"
      center
      :closable="false"
    />

    <div
      v-if="showPagination"
      class="library-items__pagination"
    >
      <ElementPagination
        background
        layout="prev, pager, next"
        :current-page="paging.currentPage"
        :page-size="paging.itemsLimit"
        :total="paging.itemsCount || 0"
        @current-change="onPageChange"
      />
    </div>
  </div>
</template>

<script>
import _debounce from 'lodash/debounce'
import { mapGetters, mapActions } from 'vuex'
import VueTypes from 'vue-types'
import { LIBRARY_TYPE } from '@/constants/libraryType'
import { UPLOAD_STATUS } from '@/constants/loadingStatus'
import Utils from '@/services/utils'
import { ItemShape } from '@/types'
import LibraryCardItem from './components/LibraryCardItem'
import LibraryCardSkeleton from './components/LibraryCardSkeleton'

export default {
  name: 'LibraryItems',

  components: {
    LibraryCardItem,
    LibraryCardSkeleton
  },

  props: {
    items: VueTypes.arrayOf(ItemShape.loose).def(() => []),
    libraryType: VueTypes.string.def(LIBRARY_TYPE.BLOCK),
    downloadCallback: VueTypes.func.def(() => {}),
    loading: VueTypes.bool.def(false),
    showStyleBulkActionsToolbar: VueTypes.bool.def(false),
    selectedStyleIds: VueTypes.array.def(() => []),
    showPagination: VueTypes.bool.def(false),
    paging: VueTypes.object.def(() => ({
      currentPage: 1,
      itemsLimit: 20,
      itemsCount: 0
    }))
  },

  data () {
    return {
      LIBRARY_TYPE
    }
  },

  computed: {
    ...mapGetters([
      'getItemUploadStatuses',
      'getJobStatuses',
      'getHasItemsReachedEnd',
      'getHasJobsReachedEnd',
      'getHasToScrollToTop'
    ]),

    /**
     * @returns {number}
     */
    skeletonCount () {
      if (!this.loading) {
        return 0
      }

      if (this.items.length === 0) {
        return this.showPagination ? this.paging.itemsLimit || 20 : 18
      }

      return this.showPagination ? 0 : 10
    },

    /**
     * @returns {boolean}
     */
    hasReachedEnd () {
      if (this.libraryType === LIBRARY_TYPE.JOB) {
        return this.getHasJobsReachedEnd
      }

      return this.getHasItemsReachedEnd()
    },

    /**
     * @returns {object}
     */
    statuses () {
      if (this.libraryType === LIBRARY_TYPE.JOB) {
        return this.getJobStatuses || {}
      }

      return this.getItemUploadStatuses || {}
    },

    /**
     * @returns {Array}
     */
    processedItems () {
      const processedItems = this.items.map(item => {
        const hasModel3D =
          item.filesDict &&
          item.filesDict.model3D &&
          item.filesDict.model3D.length > 0

        return {
          ...item,
          status: this.statuses[item.id] || null,
          hasModel3D,
          downloadCallback: this.downloadCallback || null
        }
      })

      let index = processedItems.length - 1
      const pendingArray = []

      while (index >= 0) {
        if (processedItems[index].status === UPLOAD_STATUS.PENDING) {
          const item = processedItems.splice(index, 1)
          pendingArray.push(...item)
        }

        index--
      }

      return [...pendingArray, ...processedItems]
    },

    /**
     * @returns {boolean}
     */
    hasToScrollToTop () {
      return this.getHasToScrollToTop()
    }
  },

  watch: {
    /**
     */
    items () {
      this.$nextTick().then(() => {
        this.loadMoreIfNotScrollable()
      })
    },

    /**
     * @param {boolean} newValue
     */
    loading (newValue) {
      if (!newValue) {
        this.$nextTick().then(() => {
          this.loadMoreIfNotScrollable()
        })
      }
    },

    /**
     * @param {boolean} newValue
     */
    hasToScrollToTop (newValue) {
      if (newValue) {
        this.$nextTick().then(() => {
          this.scrollToTop()
        })
      }
    },

    /**
     * @param {number} currentPage
     */
    'paging.currentPage' (currentPage) {
      if (this.showPagination && currentPage) {
        this.$nextTick().then(() => {
          this.scrollToTop()
        })
      }
    }
  },

  methods: {
    ...mapActions(['deleteJob', 'deleteItem', 'resetItemListScrollTop']),

    /**
     * @param {DragEvent} event
     */
    handleFileDrop (event) {
      this.dragLeave()

      if (this.showStyleBulkActionsToolbar) {
        return
      }

      const dataTransfer = event.dataTransfer

      if (!dataTransfer) {
        return
      }

      const isFile = Array.from(dataTransfer.types || []).includes('Files')

      if (
        this.libraryType === LIBRARY_TYPE.STYLE &&
        isFile &&
        dataTransfer.files &&
        dataTransfer.files.length > 0
      ) {
        this.$emit('files-dropped', dataTransfer.files)
      }
    },

    /**
     * @param   {File[]}   fileList
     *
     * @returns {object[]}
     */
    generateFileList (fileList) {
      return Array.from(fileList).map(item => ({
        name: item.name,
        extension: Utils.getExtensionFromFileName(item.name),
        description: Utils.getDescriptionFromFileName(item.name),
        size: item.size,
        raw: item
      }))
    },

    /**
     *
     */
    dragOver () {
      if (this.showStyleBulkActionsToolbar) {
        return
      }

      const mainListDropZone = this.$refs.mainListDropZone

      if (mainListDropZone) {
        mainListDropZone.classList.add('dragging')
      }
    },

    /**
     *
     */
    dragLeave () {
      const mainListDropZone = this.$refs.mainListDropZone

      if (mainListDropZone) {
        mainListDropZone.classList.remove('dragging')
      }
    },

    /**
     *
     * @param {object} payload
     */
    async onDeleteItem (payload) {
      const { id, name, libraryType } = payload

      if (libraryType === LIBRARY_TYPE.JOB) {
        await this.deleteJob(id)
      } else {
        await this.deleteItem(payload)
      }

      this.$message({
        showClose: true,
        message: `${name} has been deleted`
      })
    },

    /**
     *
     */
    loadMoreIfNotScrollable () {
      if (this.showPagination) {
        return
      }

      const scrollElement = this.$refs.itemListScrollable

      if (
        !scrollElement ||
        this.loading ||
        this.hasReachedEnd ||
        this.items.length === 0
      ) {
        return
      }

      const hasScrollableContent =
        scrollElement.scrollHeight > scrollElement.clientHeight + 200

      if (!hasScrollableContent) {
        this.$emit('scroll-hit-bottom')
      }
    },

    scrollHitBottom: _debounce(
      /**
       */
      function () {
        if (this.showPagination) {
          return
        }

        if (!this.hasReachedEnd && !this.loading) {
          this.$emit('scroll-hit-bottom')
        }
      },
      400
    ),

    /**
     * @param {number} currentPage
     */
    onPageChange (currentPage) {
      if (this.loading || currentPage === this.paging.currentPage) {
        return
      }

      this.$emit('page-change', currentPage)
    },

    /**
     * Scroll the card list back to its top position.
     */
    scrollToTop () {
      const scrollElement = this.$refs.itemListScrollable

      if (scrollElement) {
        scrollElement.scrollTop = 0
      }

      this.resetItemListScrollTop({
        libraryType: this.libraryType
      })
    }
  }
}
</script>

<style lang="scss" scoped>
$txt-font-size: 16px;

.library-items {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  height: 100%;
  min-height: 0;
}

.library-items__scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}

.library-items__scroll::-webkit-scrollbar {
  display: none;
}

.library-items__content {
  display: grid;
  grid-gap: spacing(2);
  grid-template-columns: repeat(auto-fill, minmax(spacing(22), 1fr));
  align-items: center;
  justify-content: space-between;
  justify-items: center;
  padding: spacing(2);
  padding-bottom: spacing(4);
}

.library-items__loading-more {
  @include text-body;

  display: flex;
  justify-content: center;
  width: percentage(100);
  padding: spacing(2) 0 spacing(4);
  color: $grey;
  text-align: center;
}

.library-items__loading-inner {
  display: flex;
  gap: spacing(1);
  align-items: center;
  padding: spacing(1) spacing(2);
  background: rgba(0, 0, 0, 0.02);
  border-radius: spacing(2);
}

.library-items__text {
  @include text-body;

  color: $grey;
  font-size: $txt-font-size;
}

.library-items__spinner {
  width: spacing(1.5);
  height: spacing(1.5);
  border: spacing(1/4) solid #e9ecef;
  border-top-color: $grey;
  border-radius: percentage(55);
  animation: spin 0.8s linear infinite;
}

.library-items__empty-state {
  flex: 0 0 auto;
  margin: spacing(2);
}

.library-items__pagination {
  position: relative;
  z-index: $z-index-layer-02;
  display: flex;
  flex: 0 0 auto;
  justify-content: center;
  padding: spacing(2) 0;
  background:
    linear-gradient(
      180deg,
      rgba(64, 158, 255, 0.03) 0%,
      rgba(255, 255, 255, 0.98) 100%
    ),
    $white;
  border-top: 1px solid $blue-light;
}

.dragging {
  background-color: $blue-light;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
